import { findCheapestShipmentInfo } from '@caresend/utils';
import { reportException } from '../../../../functions/sentry.js';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../../components/ui/Icon.vue.js';
import '../../../../functions/configuration/icons.js';
import '../../../../database/algoliaConfig/indices.js';
import '@caresend/types';
import 'mobile-device-detect';
import '../../../../functions/tracking/index.js';
import { getStore } from '../../../helpers/initStore.js';
import { getPlaceGroupIDsFromProcedures, getPlaceGroupsMissingError, getEligibleShipmentsByProcedure, getLabShipmentsForPlaceGroup, setPlaces } from './helpers/dropOff.js';
import { getTimeSlotDebugInfo } from './helpers/utilities.js';
import { getIsDebugMode } from '../../variables/helpers.js';

/**
 * Filters time slots based on drop-off place groups and sample stability for each shipment.
 * 1. Check that each time slot has all the necessary place groups.
 * 2. For each time slot, process each place group:
 *    a. Check that the sample stability is met for each shipment.
 *    b. Set the cheapest lab shipment info for each place group.
 *    c. If site collection is enabled, collect eligible place IDs.
 * 3. Update the time slots with filtered results.
 * 4. Set eligible place IDs and lab shipment info in the store.
 *
 * This layer is also responsible for returning the eligible lab places
 */
const getDropOffFilteredTimeSlots = (timeSlotsByDate, labShipmentInfo, instructionsByProcedure, siteCollectionEnabledByProcedures, draftWaypoint, procedures, siteCollectionEnabledByOffice) => {
    const isDebugMode = getIsDebugMode();
    const store = getStore();
    const placesWithCoordinates = {};
    const timeSlotsByDateObj = {};
    const cheapestLabShipmentInfoByTimeAndPlaceGroup = {};
    const dropOffPlaceGroups = getPlaceGroupIDsFromProcedures(procedures);
    Object.entries(timeSlotsByDate).forEach(([date, timeSlotsByTime]) => {
        Object.entries(timeSlotsByTime).forEach(([time, timeSlot]) => {
            var _a;
            if (timeSlot.disabled) {
                timeSlotsByDateObj[date] = {
                    ...timeSlotsByDateObj[date],
                    [time]: timeSlot,
                };
                return;
            }
            const failures = [];
            const timeSlotPlaceGroups = Object.entries(timeSlot.placeGroups);
            const placeGroupsNeeded = dropOffPlaceGroups.filter((placeGroupID) => timeSlotPlaceGroups.some(([id, _]) => id === placeGroupID));
            const missingPlaceGroupsError = getPlaceGroupsMissingError(timeSlotPlaceGroups, dropOffPlaceGroups);
            if (missingPlaceGroupsError) {
                failures.push(missingPlaceGroupsError);
            }
            else {
                placeGroupsNeeded.forEach((placeGroupID) => {
                    var _a;
                    const placeGroupAvailability = (_a = timeSlotPlaceGroups.find(([id, _]) => id === placeGroupID)) === null || _a === void 0 ? void 0 : _a[1];
                    if (!placeGroupAvailability)
                        return;
                    const availabilityTypes = ['mailIn', 'walkIn'];
                    availabilityTypes.forEach((type) => {
                        const availability = placeGroupAvailability[type];
                        if (!availability)
                            return;
                        const { isOpen, labDrawSiteCollection, labDrawPlaceIDs } = availability;
                        if (!isOpen) {
                            failures.push('Place group is not open');
                            return;
                        }
                        if (type === 'mailIn') {
                            const shipmentInfoByTime = labShipmentInfo[time];
                            const supplyShipmentInfo = shipmentInfoByTime === null || shipmentInfoByTime === void 0 ? void 0 : shipmentInfoByTime[placeGroupID];
                            const shipmentInfoByType = supplyShipmentInfo === null || supplyShipmentInfo === void 0 ? void 0 : supplyShipmentInfo.shipmentInfoByType;
                            if (!shipmentInfoByType) {
                                failures.push('No lab shipment info for place group');
                                return;
                            }
                            const eligibleShipmentsByProcedure = getEligibleShipmentsByProcedure(instructionsByProcedure, shipmentInfoByType, placeGroupID, time);
                            if (!Object.keys(eligibleShipmentsByProcedure).length) {
                                failures.push('Sample stability not met for any shipment');
                                return;
                            }
                            const labShipmentsForPlaceGroup = getLabShipmentsForPlaceGroup(eligibleShipmentsByProcedure);
                            const cheapestLabShipmentInfo = findCheapestShipmentInfo(labShipmentsForPlaceGroup);
                            if (!cheapestLabShipmentInfo) {
                                failures.push('No eligible lab shipment info');
                                reportException('No eligible lab shipment info');
                                return;
                            }
                            cheapestLabShipmentInfoByTimeAndPlaceGroup[time] = {
                                ...cheapestLabShipmentInfoByTimeAndPlaceGroup[time],
                                [placeGroupID]: cheapestLabShipmentInfo,
                            };
                        }
                        const labDrawSiteCollectionEnabled = siteCollectionEnabledByProcedures && siteCollectionEnabledByOffice && labDrawSiteCollection;
                        if (labDrawSiteCollectionEnabled) {
                            Object.entries(labDrawPlaceIDs !== null && labDrawPlaceIDs !== void 0 ? labDrawPlaceIDs : {}).forEach(([placeID, coordinates]) => {
                                placesWithCoordinates[placeID] = { lat: coordinates.lat, lng: coordinates.lng };
                            });
                        }
                    });
                });
            }
            const timeSlotsForTime = (_a = timeSlotsByDateObj[date]) !== null && _a !== void 0 ? _a : {};
            if (!failures.length)
                timeSlotsForTime[time] = timeSlot;
            if (failures.length && isDebugMode)
                timeSlotsForTime[time] = getTimeSlotDebugInfo(timeSlot, failures);
            timeSlotsByDateObj[date] = timeSlotsForTime;
        });
    });
    if (Object.keys(placesWithCoordinates).length) {
        store.commit('schedulingFlow/SET_IS_BRICK_AND_MORTAR_ELIGIBLE', true);
    }
    const { maxPlacesForOffice } = store.state.schedulingFlow;
    if (draftWaypoint.location) {
        setPlaces(placesWithCoordinates, draftWaypoint.location, maxPlacesForOffice);
    }
    else {
        reportException('Missing location during time slot calculation for draft waypoint '
            + `${draftWaypoint.id}`);
    }
    store.commit('schedulingFlow/SET_CHEAPEST_LAB_SHIPMENT_INFO', cheapestLabShipmentInfoByTimeAndPlaceGroup);
    return timeSlotsByDateObj;
};

export { getDropOffFilteredTimeSlots };
